import _ from 'lodash'

/**
 * User Storage service for interacting with the logged user.
 *
 * There are many ways to leverage this service:
 * - In routes, use the `loggedUser` resolve inherited from the root `app` route
 * - The `loggedUser` resolve is available in state transition hooks as well
 * - In controllers of route components, bind `loggedUser` in the component and
 *   use it as `this.loggedUser` in the controller or as `$ctrl.loggedUser` in
 *   the template
 * - In controllers and templates of non-route components, use `UserStorage.getUser()`
 *   async method.
 *
 * @example <caption>In routes</caption>
 *   resolve: {
 *     myResolveVar: function(loggedUser) { 'ngInject'; ... }
 *   }
 *
 * @example <caption>In transition hooks</caption>
 *   $transitions.onBefore({...}, trans => trans.injector().getAsync('loggedUser').then(...));
 *
 * @example <caption>In controllers and templates of route components</caption>
 *   // In route definition; makes loggedUser available in the component's controller
 *   bindings: {
 *     loggedUser: '<'
 *   }
 *
 * @example <caption>In controllers and templates of non-route components</caption>
 *   $onInit() {
 *     UserStorage.getUser().then(user => {
 *       this.isAdmin = UserStorage.isAdmin();
 *       this.username = user.username;
 *     })
 *   }
 *
 * @type {ngService}
 */
export class UserStorage {
  static ngType = 'service'

  constructor($injector, $rootScope, $state, User, TmrLocalStorage) {
    'ngInject'
    Object.assign(this, {
      $injector,
      $rootScope,
      $state,
      User,
      TmrLocalStorage,
    })
    this.accessControlCache = {}
    $rootScope.$on('logout', () => this.onLogout())
  }

  /**
   * Gets current user resource (asyncronous version of {UserStorage#getUserSync}).
   *
   * @returns {Promise<User>} A promise of the current user
   */
  getUser() {
    return this.getUserSync().$promise
  }

  /**
   * Gets the current user resource.
   *
   * If user data does not exist, an empty user is created; moreover, if we
   * have a stored token, user data will be automatically loaded.
   *
   * @returns {User} The current user, possibly not loaded. You cann access promise in `user.$promise`
   */
  getUserSync() {
    this.user = this.user ?? this.User.create()

    if (typeof this.user.$promise !== 'object') {
      // Add $promise property immediately: ngResource adds its own only after first request.
      this.user.$promise = this.TmrLocalStorage.get('authToken')
        ? this.user.$getLogged().catch(err => this.getLoggedUserError(err))
        : Promise.resolve(this.user)
    }

    return this.user
  }

  getLoggedUserError(err) {
    // If authToken is bad or expired, remove it.
    // This will trigger a redirect to login.
    if (err.status == 400 || err.status == 401) {
      this.TmrLocalStorage.clear('authToken')
      return this.user
    }
    return Promise.reject(err)
  }

  /**
   * Clears current user.
   */
  clearUser() {
    if (this.user !== undefined) {
      this.user.id = null // Workaround for services that watch the id.
      this.user = this.User.create()
    }
  }

  /**
   * Check if there is an active user.
   */
  isAuthenticated() {
    return this.user !== undefined && this.user.id ? true : false
  }

  /**
   * Get current user name.
   */
  getUsername() {
    return this.isAuthenticated() ? this.user.username : undefined
  }

  /**
   * Returns true if the user has a certain capability.
   */
  hasCapability(capability) {
    return (
      this.isAdmin() ||
      this.user.roles.some(role => role.capabilities.indexOf(capability) > -1)
    )
  }

  /**
   * Returns true if the user has any of an array of capabilities.
   */
  hasAnyCapability(capabilities) {
    return (
      this.isAdmin() ||
      this.user.roles.some(
        role => !!_.intersection(capabilities, role.capabilities).length
      )
    )
  }

  /**
   * Returns true if the user ha access to a state.
   *
   * @param {string|object} state A state name (also relative), or a state object
   */
  access(state) {
    if (typeof state === 'string') state = this.$state.get(state)
    if (state === null || state.accessControl === false) return false
    if (typeof state.accessControl === 'string') {
      this.accessControlCache[state.accessControl] =
        this.accessControlCache[state.accessControl] ??
        this.$injector.get(state.accessControl)
      const result = this.accessControlCache[state.accessControl](state)
      if (typeof result !== 'boolean')
        throw new Error(
          `accessControl service ${state.accessControl} in state ${state.name} must return true or false`
        )
      return result
    }
    if (Array.isArray(state.accessControl))
      return (
        state.accessControl.length == 0 ||
        this.hasAnyCapability(state.accessControl)
      )
  }

  /**
   * Returns true if the user is an administrator.
   */
  isAdmin() {
    return this.isAuthenticated() && this.user.admin === true
  }

  /**
   * Returns true if the user is allowed to debug the application
   *
   * Conditions:
   * - mock is enabled; OR
   * - mock is disabled, but the user has the debug capability (ignoring if user is admin)
   */
  isDebugger() {
    return (
      process.env.includeDevel ||
      (this.isAuthenticated() &&
        this.user.roles.some(role =>
          role.capabilities.includes('Debug application')
        ))
    )
  }

  /**
   * Clears user when logging out.
   */
  onLogout() {
    this.clearUser()
  }
}
